import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import {
  Rocket, FlaskConical, Users, Leaf, Calendar, Clock,
  BarChart3, ChevronRight, Star, Zap, Globe, ArrowRight
} from "lucide-react";

// ─── Canvas Starfield ─────────────────────────────────────────────────────────
function Starfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // Generate stars
    const STAR_COUNT = 320;
    const stars = Array.from({ length: STAR_COUNT }, () => ({
      x:     Math.random() * canvas.width,
      y:     Math.random() * canvas.height,
      r:     Math.random() * 1.6 + 0.2,
      alpha: Math.random() * 0.7 + 0.3,
      speed: Math.random() * 0.3 + 0.05,
      twinkle: Math.random() * Math.PI * 2,
      twinkleSpeed: Math.random() * 0.02 + 0.005,
    }));

    // Shooting stars
    const shoots = [];
    const addShoot = () => {
      if (shoots.length < 3 && Math.random() < 0.008) {
        shoots.push({
          x: Math.random() * canvas.width * 0.6,
          y: Math.random() * canvas.height * 0.4,
          len: Math.random() * 120 + 60,
          speed: Math.random() * 8 + 5,
          alpha: 1,
          angle: Math.PI / 5 + (Math.random() - 0.5) * 0.3,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Gradient background
      const grad = ctx.createRadialGradient(
        canvas.width * 0.5, canvas.height * 0.3, 0,
        canvas.width * 0.5, canvas.height * 0.3, canvas.width * 0.8
      );
      grad.addColorStop(0,   "rgba(10,15,40,1)");
      grad.addColorStop(0.5, "rgba(5,8,25,1)");
      grad.addColorStop(1,   "rgba(2,4,15,1)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Nebula glow
      const neb = ctx.createRadialGradient(
        canvas.width * 0.7, canvas.height * 0.25, 0,
        canvas.width * 0.7, canvas.height * 0.25, canvas.width * 0.4
      );
      neb.addColorStop(0,   "rgba(0,100,255,0.06)");
      neb.addColorStop(0.5, "rgba(100,0,200,0.04)");
      neb.addColorStop(1,   "rgba(0,0,0,0)");
      ctx.fillStyle = neb;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const neb2 = ctx.createRadialGradient(
        canvas.width * 0.15, canvas.height * 0.7, 0,
        canvas.width * 0.15, canvas.height * 0.7, canvas.width * 0.35
      );
      neb2.addColorStop(0,   "rgba(0,200,150,0.05)");
      neb2.addColorStop(1,   "rgba(0,0,0,0)");
      ctx.fillStyle = neb2;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Stars
      stars.forEach(s => {
        s.twinkle += s.twinkleSpeed;
        s.y -= s.speed * 0.1;
        if (s.y < 0) { s.y = canvas.height; s.x = Math.random() * canvas.width; }
        const tw = Math.sin(s.twinkle) * 0.3 + 0.7;
        ctx.save();
        ctx.globalAlpha = s.alpha * tw;
        ctx.shadowBlur  = s.r > 1.2 ? 8 : 3;
        ctx.shadowColor = "rgba(150,180,255,0.8)";
        ctx.fillStyle   = `rgba(220,230,255,${s.alpha * tw})`;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      // Shooting stars
      addShoot();
      for (let i = shoots.length - 1; i >= 0; i--) {
        const sh = shoots[i];
        const dx = Math.cos(sh.angle) * sh.len;
        const dy = Math.sin(sh.angle) * sh.len;
        const g  = ctx.createLinearGradient(sh.x, sh.y, sh.x + dx, sh.y + dy);
        g.addColorStop(0,   `rgba(255,255,255,${sh.alpha})`);
        g.addColorStop(1,   "rgba(255,255,255,0)");
        ctx.save();
        ctx.strokeStyle = g;
        ctx.lineWidth   = 1.5;
        ctx.globalAlpha = sh.alpha;
        ctx.beginPath();
        ctx.moveTo(sh.x, sh.y);
        ctx.lineTo(sh.x + dx, sh.y + dy);
        ctx.stroke();
        ctx.restore();
        sh.x += Math.cos(sh.angle) * sh.speed;
        sh.y += Math.sin(sh.angle) * sh.speed;
        sh.alpha -= 0.018;
        if (sh.alpha <= 0) shoots.splice(i, 1);
      }

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 w-full h-full" style={{ zIndex: 0 }} />;
}

// ─── Orbit ring decorative ────────────────────────────────────────────────────
function OrbitRing({ size, duration, delay, color = "rgba(0,150,255,0.15)" }) {
  return (
    <motion.div
      className="absolute rounded-full border pointer-events-none"
      style={{
        width: size, height: size,
        borderColor: color,
        top: "50%", left: "50%",
        x: "-50%", y: "-50%",
      }}
      animate={{ rotate: 360 }}
      transition={{ duration, repeat: Infinity, ease: "linear", delay }}
    >
      <motion.div
        className="absolute w-2 h-2 rounded-full"
        style={{ background: color.replace("0.15", "0.8"), top: -4, left: "50%" }}
      />
    </motion.div>
  );
}

// ─── Module card ─────────────────────────────────────────────────────────────
const MODULES = [
  {
    icon:  Rocket,
    label: "Proyectos I+D",
    desc:  "Fichas de proyecto, fichas de cierre, exports PDF/DOCX.",
    color: "from-blue-500/20 to-cyan-500/10",
    border:"border-blue-500/30",
    glow:  "rgba(59,130,246,0.25)",
    span:  "col-span-2",
  },
  {
    icon:  Users,
    label: "Talent OS",
    desc:  "1:1s, objetivos y desarrollo del equipo.",
    color: "from-violet-500/20 to-purple-500/10",
    border:"border-violet-500/30",
    glow:  "rgba(139,92,246,0.2)",
    span:  "",
  },
  {
    icon:  Leaf,
    label: "ESG · EINF",
    desc:  "Métricas sostenibilidad y reporting.",
    color: "from-emerald-500/20 to-teal-500/10",
    border:"border-emerald-500/30",
    glow:  "rgba(16,185,129,0.2)",
    span:  "",
  },
  {
    icon:  FlaskConical,
    label: "Portfolio I+D",
    desc:  "Airtable sync bidireccional, iniciativas y KPIs.",
    color: "from-amber-500/20 to-orange-500/10",
    border:"border-amber-500/30",
    glow:  "rgba(245,158,11,0.2)",
    span:  "",
  },
  {
    icon:  Calendar,
    label: "Weeklys",
    desc:  "Reuniones semanales, backlog de acuerdos.",
    color: "from-pink-500/20 to-rose-500/10",
    border:"border-pink-500/30",
    glow:  "rgba(236,72,153,0.2)",
    span:  "",
  },
  {
    icon:  Clock,
    label: "Timesheets",
    desc:  "Imputación de horas con firma digital.",
    color: "from-sky-500/20 to-blue-500/10",
    border:"border-sky-500/30",
    glow:  "rgba(14,165,233,0.2)",
    span:  "",
  },
  {
    icon:  BarChart3,
    label: "MyTime Equipo",
    desc:  "Vacaciones y teletrabajo del equipo en tiempo real.",
    color: "from-indigo-500/20 to-violet-500/10",
    border:"border-indigo-500/30",
    glow:  "rgba(99,102,241,0.2)",
    span:  "",
  },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.2 } },
};
const item = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  show:   { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.25, 0.4, 0.25, 1] } },
};

// ─── Main component ──────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate          = useNavigate();
  const { user }          = useAuth();
  const [hovered, setHovered] = useState(null);

  // Already authenticated → go to dashboard
  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);

  return (
    <div
      className="relative min-h-screen overflow-x-hidden"
      style={{ background: "#020409" }}
    >
      <Starfield />

      {/* ── Header ── */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative z-20 flex items-center justify-between px-6 md:px-12 py-5"
      >
        <div className="flex items-center gap-3">
          <img src="/valoriza-logo.svg" alt="Valoriza" className="h-7 w-auto object-contain" />
        </div>
        <motion.button
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate("/login")}
          className="text-sm text-white/60 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-5 py-2 transition-all backdrop-blur"
          data-testid="header-login-btn"
        >
          Acceder
        </motion.button>
      </motion.header>

      {/* ── Hero ── */}
      <motion.section
        className="relative z-10 flex flex-col items-center justify-center text-center min-h-[92vh] px-6 pb-20"
      >
        {/* Planet decoration — right side, behind text */}
        <div className="absolute right-[-60px] top-1/2 -translate-y-1/2 pointer-events-none select-none opacity-50 hidden lg:block"
          style={{ width: 480, height: 480 }}>
          <OrbitRing size={260} duration={30} delay={0}   color="rgba(0,150,255,0.2)" />
          <OrbitRing size={380} duration={50} delay={5}   color="rgba(140,80,255,0.12)" />
          <OrbitRing size={480} duration={80} delay={10}  color="rgba(0,200,150,0.08)" />
          {/* CSS planet */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.34, 1.56, 0.64, 1] }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              width: 120, height: 120,
              background: "radial-gradient(circle at 35% 35%, #60a5fa 0%, #1d4ed8 40%, #0f172a 100%)",
              boxShadow: "0 0 60px rgba(59,130,246,0.5), 0 0 120px rgba(59,130,246,0.2), inset 0 0 30px rgba(0,0,0,0.5)",
            }}
          >
            {/* Atmosphere glow */}
            <div className="absolute inset-[-8px] rounded-full"
              style={{ background: "radial-gradient(circle, transparent 55%, rgba(96,165,250,0.15) 75%, transparent 100%)" }} />
          </motion.div>
        </div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 backdrop-blur px-4 py-1.5 text-xs text-blue-300 font-medium"
        >
          <Star className="w-3 h-3 fill-blue-400 text-blue-400" />
          Plataforma de Operaciones de Innovación
          <Star className="w-3 h-3 fill-blue-400 text-blue-400" />
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
          className="relative text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-black tracking-tighter text-white mb-6 leading-none"
          style={{ textShadow: "0 0 80px rgba(0,100,255,0.3)" }}
        >
          Innovation
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #60a5fa 0%, #a78bfa 40%, #34d399 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            OS
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65, duration: 0.7 }}
          className="max-w-2xl text-lg md:text-xl text-white/50 mb-12 leading-relaxed"
        >
          El sistema operativo de la innovación de Valoriza.
          <br />
          Proyectos I+D, ESG, talento y operaciones en una sola plataforma.
        </motion.p>

        {/* CTA buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex flex-col sm:flex-row items-center gap-4"
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(0,120,255,0.5)" }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/login")}
            data-testid="hero-cta-btn"
            className="group relative flex items-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold text-white overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)" }}
          >
            <Rocket className="w-5 h-5 group-hover:rotate-12 transition-transform" />
            Acceder a la plataforma
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            <motion.div
              className="absolute inset-0 rounded-2xl"
              style={{ background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)" }}
            />
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => document.getElementById("modules")?.scrollIntoView({ behavior: "smooth" })}
            className="flex items-center gap-2 rounded-2xl px-8 py-4 text-base font-medium text-white/60 hover:text-white border border-white/10 hover:border-white/25 backdrop-blur transition-all"
          >
            Explorar módulos
            <ChevronRight className="w-4 h-4" />
          </motion.button>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
            className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center pt-1.5"
          >
            <div className="w-1 h-1.5 rounded-full bg-white/40" />
          </motion.div>
        </motion.div>
      </motion.section>

      {/* ── Stats bar ── */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6 }}
        className="relative z-10 border-y border-white/5 backdrop-blur"
        style={{ background: "rgba(255,255,255,0.02)" }}
      >
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: "7+", label: "Módulos integrados", icon: Zap },
            { value: "I+D", label: "Proyectos con fichas PDF", icon: FlaskConical },
            { value: "ESG", label: "Reporting sostenibilidad", icon: Leaf },
            { value: "SSO", label: "Acceso corporativo Microsoft", icon: Globe },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              className="flex flex-col items-center gap-1"
            >
              <s.icon className="w-5 h-5 text-blue-400/60 mb-1" />
              <span className="text-2xl font-black text-white">{s.value}</span>
              <span className="text-xs text-white/40">{s.label}</span>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* ── Modules bento grid ── */}
      <section id="modules" className="relative z-10 max-w-6xl mx-auto px-6 py-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p className="text-blue-400/70 text-sm font-medium tracking-widest uppercase mb-3">
            Ecosistema completo
          </p>
          <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter">
            Todo lo que necesita
            <br />
            <span style={{
              background: "linear-gradient(135deg, #60a5fa, #a78bfa)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
            }}>tu equipo de innovación</span>
          </h2>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
        >
          {MODULES.map((mod, i) => (
            <motion.div
              key={i}
              variants={item}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`relative rounded-2xl border p-5 cursor-default overflow-hidden backdrop-blur-sm
                bg-gradient-to-br ${mod.color} ${mod.border}
                ${mod.span === "col-span-2" ? "col-span-2 md:col-span-2" : ""}
                transition-all duration-300`}
              style={{
                boxShadow: hovered === i ? `0 0 40px ${mod.glow}` : "none",
              }}
            >
              {/* Glow overlay on hover */}
              <AnimatePresence>
                {hovered === i && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{ background: `radial-gradient(circle at 30% 30%, ${mod.glow} 0%, transparent 70%)` }}
                  />
                )}
              </AnimatePresence>

              <div className="relative z-10">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3
                  bg-gradient-to-br ${mod.color} border ${mod.border}`}>
                  <mod.icon className="w-5 h-5 text-white/80" />
                </div>
                <h3 className="font-bold text-white text-sm mb-1">{mod.label}</h3>
                <p className="text-white/40 text-xs leading-relaxed">{mod.desc}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── Final CTA ── */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center px-6 py-28 max-w-3xl mx-auto"
      >
        <motion.div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            background: "radial-gradient(ellipse 80% 60% at 50% 50%, rgba(0,80,200,0.12) 0%, transparent 70%)"
          }}
        />
        <h2 className="text-4xl md:text-5xl font-black text-white tracking-tighter mb-5">
          El futuro de la innovación
          <br />
          <span style={{
            background: "linear-gradient(135deg, #34d399, #60a5fa)",
            WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text"
          }}>ya está aquí</span>
        </h2>
        <p className="text-white/40 text-base mb-10 max-w-xl mx-auto">
          Accede con tu cuenta corporativa de Microsoft y empieza a gestionar la innovación de Valoriza desde un solo lugar.
        </p>
        <motion.button
          whileHover={{ scale: 1.06, boxShadow: "0 0 60px rgba(0,100,255,0.4)" }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate("/login")}
          data-testid="final-cta-btn"
          className="group inline-flex items-center gap-3 rounded-2xl px-10 py-5 text-lg font-bold text-white"
          style={{ background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)" }}
        >
          <Rocket className="w-5 h-5 group-hover:rotate-12 transition-transform" />
          Iniciar sesión
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </motion.button>
      </motion.section>

      {/* ── Footer ── */}
      <footer className="relative z-10 border-t border-white/5 text-center py-8 text-white/25 text-xs">
        © 2025 Valoriza Servicios Medioambientales · Innovation OS · Todos los derechos reservados
      </footer>
    </div>
  );
}
